import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  usePropertyField,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useSiteCtaStyles } from '../hooks/useSiteCtaStyles';

const APP_STORE_BADGE_URL =
  'https://lirp.cdn-website.com/ceae0154/dms3rep/multi/opt/Apple+App+Store+download+button-1920w.png';
const GOOGLE_PLAY_BADGE_URL =
  'https://lirp.cdn-website.com/ceae0154/dms3rep/multi/opt/Google+Store+download+button-1920w.png';
const BADGE_SIZES = '180px';

const portalCtaDef: SlotGroupDefinition = {
  label: 'Portal CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      suggestedFields: ['property_resident_portal_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Login Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const helpCtaDef: SlotGroupDefinition = {
  label: 'Phone CTA',
  fields: {
    text: {
      label: 'Phone Text',
      type: 'text',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      defaultValue: '',
    },
    url: {
      label: 'Phone Number',
      type: 'url',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const appStoreCtaDef: SlotGroupDefinition = {
  label: 'App Store Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://apps.apple.com/us/app/loft-living/id333244247',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const googlePlayCtaDef: SlotGroupDefinition = {
  label: 'Google Play Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://play.google.com/store/apps/details?id=com.activebuilding.residents',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function toTelUrl(raw: string | null | undefined): string {
  if (!raw) return '';
  if (raw.startsWith('tel:')) return raw;
  const digits = raw.replace(/[^+\d]/g, '');
  return digits ? `tel:${digits}` : raw;
}

/**
 * Aion Resident Access section — two bordered action panels (portal login +
 * phone) with a Loft mobile-app download row beneath, matching the reference
 * layout used on Aion property sites.
 */
export function ResidentAccess() {
  useSiteCtaStyles();
  const callTrackingPhone = (usePropertyField('property_call_tracking_phone') as string) || '';
  const defaultPhone = (usePropertyField('property_phone') as string) || '';
  const fallbackPhone = callTrackingPhone || defaultPhone;

  const heading = useContentToken('residentaccess.heading');
  const portalTitle = useContentToken('residentaccess.portal_title');
  const portalCta = useSlotGroup('residentaccess.portal_cta', portalCtaDef);
  const helpTitle = useContentToken('residentaccess.help_title');
  const helpCta = useSlotGroup('residentaccess.help_cta', helpCtaDef);
  const appHeading = useContentToken('residentaccess.app_heading');
  const appStoreCta = useSlotGroup('residentaccess.app_store_cta', appStoreCtaDef);
  const googlePlayCta = useSlotGroup('residentaccess.google_play_cta', googlePlayCtaDef);

  const helpPhoneHref =
    toTelUrl(helpCta.url) || (fallbackPhone ? `tel:${fallbackPhone.replace(/\D/g, '')}` : '');
  const helpPhoneDisplay =
    formatPhone(helpCta.text) || formatPhone(helpCta.url) || formatPhone(fallbackPhone);
  const appStoreHref = appStoreCta.url || '';
  const googlePlayHref = googlePlayCta.url || '';

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section ref={sectionRef} className="reveal-fade-up ra-section">
      <div className="ra-container">
        <EditableToken path="residentaccess.heading" as="h2" className="sr-only">
          {heading || 'Resident Access'}
        </EditableToken>

        <div className="ra-grid">
          <div className="ra-card">
            <EditableToken path="residentaccess.portal_title" as="h3" className="ra-card-title">
              {portalTitle || 'Resident Portal'}
            </EditableToken>
            {portalCta.url ? (
              <EditableSlotGroup groupPath="residentaccess.portal_cta" definition={portalCtaDef}>
                <a
                  href={portalCta.url}
                  target={portalCta._target || '_blank'}
                  rel={portalCta._target === '_self' ? undefined : 'noopener noreferrer'}
                  className="ra-login-btn"
                >
                  {portalCta.label || 'Login Now'}
                </a>
              </EditableSlotGroup>
            ) : (
              <span className="ra-login-btn ra-login-btn--disabled">Portal URL Not Available</span>
            )}
          </div>

          <div className="ra-card">
            <EditableToken path="residentaccess.help_title" as="h3" className="ra-card-title">
              {helpTitle || 'Need help? Call Us'}
            </EditableToken>
            <EditableSlotGroup groupPath="residentaccess.help_cta" definition={helpCtaDef}>
              <a
                href={helpPhoneHref || '#'}
                target={helpCta._target || '_self'}
                className="dniphone dniphonehref ra-phone"
              >
                {helpPhoneDisplay || 'Call Us'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        <div className="ra-app">
          <EditableToken path="residentaccess.app_heading" as="h3" className="ra-app-heading">
            {appHeading || 'Download the Loft Resident Mobile App'}
          </EditableToken>
          <div className="ra-app-badges">
            <EditableSlotGroup groupPath="residentaccess.app_store_cta" definition={appStoreCtaDef}>
              {appStoreHref ? (
                <a
                  href={appStoreHref}
                  target={appStoreCta._target || '_blank'}
                  rel="noopener noreferrer"
                  className="ra-badge-link"
                >
                  <VibesitesImage
                    url={APP_STORE_BADGE_URL}
                    alt="Download on the App Store"
                    sizes={BADGE_SIZES}
                    className="ra-badge-img"
                  />
                </a>
              ) : (
                <span className="ra-badge-link ra-badge-link--muted">
                  <VibesitesImage
                    url={APP_STORE_BADGE_URL}
                    alt="Download on the App Store"
                    sizes={BADGE_SIZES}
                    className="ra-badge-img"
                  />
                </span>
              )}
            </EditableSlotGroup>
            <EditableSlotGroup
              groupPath="residentaccess.google_play_cta"
              definition={googlePlayCtaDef}
            >
              {googlePlayHref ? (
                <a
                  href={googlePlayHref}
                  target={googlePlayCta._target || '_blank'}
                  rel="noopener noreferrer"
                  className="ra-badge-link"
                >
                  <VibesitesImage
                    url={GOOGLE_PLAY_BADGE_URL}
                    alt="Get it on Google Play"
                    sizes={BADGE_SIZES}
                    className="ra-badge-img"
                  />
                </a>
              ) : (
                <span className="ra-badge-link ra-badge-link--muted">
                  <VibesitesImage
                    url={GOOGLE_PLAY_BADGE_URL}
                    alt="Get it on Google Play"
                    sizes={BADGE_SIZES}
                    className="ra-badge-img"
                  />
                </span>
              )}
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .ra-section {
          padding: 2.5rem 0 4rem;
          background: var(--vibe-color-background, #ffffff);
        }
        @media (min-width: 768px) {
          .ra-section { padding: 3rem 0 5rem; }
        }

        .ra-container {
          max-width: 56rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) {
          .ra-container { padding: 0 1.5rem; }
        }

        .ra-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 768px) {
          .ra-grid {
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
            align-items: stretch;
          }
        }

        .ra-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          gap: 1.25rem;
          padding: 2.75rem 2rem;
          background: var(--vibe-color-background, #ffffff);
          border: 1px solid var(--vibe-color-primary, #0B1A3A);
          border-radius: 2px;
          box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);
        }

        .ra-card-title {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.25rem, 2vw, 1.5rem);
          font-weight: 700;
          color: var(--vibe-color-primary, #0B1A3A);
          margin: 0;
          line-height: 1.25;
        }

        .ra-login-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 10.5rem;
          padding: 0.75rem 1.75rem;
          border-radius: 999px;
          background: var(--vibe-color-primary, #0B1A3A);
          color: var(--vibe-color-text-on-primary, #ffffff);
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          font-weight: 600;
          text-decoration: none;
          transition: background-color 0.2s ease, transform 0.2s ease;
        }
        .ra-login-btn:hover {
          background: var(--vibe-color-surface, var(--vibe-color-primary));
          color: var(--site-cta-ink-on-surface, #ffffff);
        }
        .ra-login-btn--disabled,
        .ra-login-btn--disabled:hover {
          background: transparent;
          color: var(--vibe-color-text-muted);
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          cursor: default;
          opacity: 0.7;
        }

        .ra-phone {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.35rem, 2.5vw, 1.75rem);
          font-weight: 700;
          color: var(--vibe-color-text, #111111);
          text-decoration: none;
          letter-spacing: 0.01em;
          line-height: 1.2;
        }
        .ra-phone:hover {
          color: var(--vibe-color-primary, #0B1A3A);
        }

        .ra-app {
          margin-top: 3rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 1.25rem;
        }
        @media (min-width: 768px) {
          .ra-app { margin-top: 3.5rem; }
        }

        .ra-app-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.35rem, 2.5vw, 1.75rem);
          font-weight: 700;
          color: var(--vibe-color-primary, #0B1A3A);
          margin: 0;
          line-height: 1.3;
          max-width: 28rem;
        }

        .ra-app-badges {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 0.85rem 1rem;
        }

        .ra-badge-link {
          display: inline-flex;
          line-height: 0;
          text-decoration: none;
          transition: transform 0.25s ease, opacity 0.25s ease;
        }
        .ra-badge-link:hover {
          transform: translateY(-2px);
          opacity: 0.92;
        }
        .ra-badge-link--muted {
          cursor: default;
        }

        .ra-badge-img {
          height: 2.75rem;
          width: auto;
          display: block;
          object-fit: contain;
        }
        @media (min-width: 768px) {
          .ra-badge-img { height: 3.25rem; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ResidentAccess',
  displayName: 'Resident Access',
  tokens: {
    'residentaccess.heading': {
      label: 'Section Heading',
      type: 'text',
      defaultValue: 'Resident Access',
    },
    'residentaccess.portal_title': {
      label: 'Portal Card Title',
      type: 'text',
      defaultValue: 'Resident Portal',
    },
    'residentaccess.help_title': {
      label: 'Help Card Title',
      type: 'text',
      defaultValue: 'Need help? Call Us',
    },
    'residentaccess.app_heading': {
      label: 'App Download Heading',
      type: 'text',
      defaultValue: 'Download the Loft Resident Mobile App',
    },
  },
  slotGroups: {
    'residentaccess.portal_cta': portalCtaDef,
    'residentaccess.help_cta': helpCtaDef,
    'residentaccess.app_store_cta': appStoreCtaDef,
    'residentaccess.google_play_cta': googlePlayCtaDef,
  },
};

(ResidentAccess as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ResidentAccess;
